import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Circle, Trash2, X } from 'lucide-react';
import { CustomFieldDefinition, apiService } from '../services/api';
import { SortField, SortState } from '../types/sorting';

interface TableHeaderProps {
  visibleColumns: string[];
  columnWidths: Record<string, number>;
  sortState: SortState;
  customFields: CustomFieldDefinition[];
  selectedIds: Set<string>;
  onSort: (field: SortField) => void;
  onMouseDown: (e: React.MouseEvent, columnKey: string) => void;
  onReorderColumns?: (newOrder: string[]) => void;
  getCustomFieldDefinition: (fieldId: string) => CustomFieldDefinition | undefined;
  onRefreshAll?: () => void;
  onToggleSelectAll: (checked: boolean) => void;
  onBulkDelete: () => Promise<void>;
  onToggleColumn?: (columnId: string) => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  visibleColumns,
  columnWidths,
  sortState,
  selectedIds,
  customFields,
  onSort,
  onMouseDown,
  onReorderColumns,
  getCustomFieldDefinition,
  onRefreshAll,
  onToggleSelectAll,
  onBulkDelete,
  onToggleColumn
}) => {
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [showRefreshAllModal, setShowRefreshAllModal] = useState(false);
  const [jobQueueStatus, setJobQueueStatus] = useState<{
    pending: number;
    running: number;
  } | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Fetch job queue status
  const fetchJobQueueStatus = async () => {
    try {
      const status = await apiService.getJobQueueStatus();
      setJobQueueStatus(status);
    } catch (error) {
      console.error('Error fetching job queue status:', error);
    }
  };

  useEffect(() => {
    fetchJobQueueStatus();
    // Poll every 10 seconds for real-time updates
    const interval = setInterval(fetchJobQueueStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const getJobStatusText = () => {
    if (!jobQueueStatus) return '0 jobs in queue';
    const total = jobQueueStatus.running + jobQueueStatus.pending;
    return total === 0 ? '0 jobs in queue' : `${total} job${total > 1 ? 's' : ''} in queue`;
  };

  // Show refresh all confirmation modal
  const handleRefreshAllClick = () => {
    setShowRefreshAllModal(true);
  };

  // Confirm and execute refresh all
  const handleConfirmRefreshAll = async () => {
    setShowRefreshAllModal(false);
    if (refreshingAll) return;
    
    setRefreshingAll(true);
    try {
      console.log('🔄 Refreshing all profiles...');
      const result = await apiService.refreshAllProfiles();
      
      if (result.success) {
        console.log(`✅ Refresh all jobs queued: ${result.queuedJobs}/${result.totalProfiles} jobs created`);
        console.log(`📝 Jobs created:`, result.jobs);
        
        // Show success message
        alert(`Successfully queued ${result.queuedJobs} refresh jobs!\n\nJobs will be processed one at a time in the background.\nYou can monitor progress in the LinkedIn status indicator.`);
        
        // Call the parent refresh function if provided
        if (onRefreshAll) {
          onRefreshAll();
        }
      } else {
        console.error('❌ Refresh all failed:', result);
        alert(`Failed to queue refresh jobs. Please try again.`);
      }
    } catch (error) {
      console.error('❌ Error refreshing all profiles:', error);
      alert('Failed to refresh all profiles. Please try again.');
    } finally {
      setRefreshingAll(false);
    }
  };

  // Cancel refresh all
  const handleCancelRefreshAll = () => {
    setShowRefreshAllModal(false);
  };

  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <ArrowUpDown size={14} />;
    if (sortState.direction === 'asc') return <ArrowUp size={14} />;
    if (sortState.direction === 'desc') return <ArrowDown size={14} />;
    return <ArrowUpDown size={14} />;
  };

  const columnLabels: Record<string, string> = {
    position: 'Position',
    tag: 'Tag',
    stage: 'Stage',
    industry: 'Industry', 
    location: 'Location',
    followerCount: 'Followers',
    priorityScore: 'Priority',
    status: 'Status',
    owner: 'Owner',
    lastTouchAt: 'Last Touch',
    linkedinUrl: 'LinkedIn',
    profileUrl: 'Profile URL',
    lastPostExcerpt: 'Last Post',
    lastPostDate: 'Last Post Date',
    nextAction: 'Next Action',
    notes: 'Latest Note',
    company: 'Company',
    xUrl: 'X',
    substackUrl: 'Substack',
    accelerator: 'Accelerator',
    vcFirm: 'VC Firm',
    contactPhone: 'Contact Phone',
    lastEditedBy: 'Last Edited By',
    type: 'Type',
    no: 'No',
    update: 'Update'
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnId);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedColumn && draggedColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedColumn || draggedColumn === targetColumnId || !onReorderColumns) {
      setDraggedColumn(null);
      return;
    }

    const newOrder = [...visibleColumns];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedColumn(null);
      return;
    }

    // Remove dragged column from its current position
    newOrder.splice(draggedIndex, 1);
    // Insert it at the target position
    newOrder.splice(targetIndex, 0, draggedColumn);
    
    onReorderColumns(newOrder);
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const renderColumnHeader = (columnId: string) => {
    const isCoreColumn = ['name', 'email', 'company', 'linkedinUrl', 'position'].includes(columnId);
    const isDragging = draggedColumn === columnId;
    const isDragOver = dragOverColumn === columnId;

    // Check core columns FIRST
    if (isCoreColumn) {
      const labels = { name: 'Name', email: 'Email', company: 'Company', linkedinUrl: 'LinkedIn', position: 'Position' };
      const isName = columnId === 'name';
      return (
        <th 
          key={columnId}
          className={`${columnId}-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
          style={{ width: columnWidths[columnId] }}
          data-column={columnId}
          draggable={isName ? false : !!onReorderColumns}
          onDragStart={(e) => handleDragStart(e, columnId)}
          onDragOver={(e) => handleDragOver(e, columnId)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, columnId)}
          onDragEnd={handleDragEnd}
        >
          <div className="header-content">
            <span className="column-label">{labels[columnId as keyof typeof labels]}</span>
            <button
              className="sort-button"
              onClick={() => onSort(columnId as SortField)}
              title={`Sort by ${labels[columnId as keyof typeof labels]}`}
            >
              {getSortIcon(columnId as SortField)}
            </button>
            {onToggleColumn && !isName && (
              <button
                className="column-close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleColumn(columnId);
                }}
                title={`Hide ${labels[columnId as keyof typeof labels]}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, columnId)} />
        </th>
      );
    }

    // Check other built-in columns before custom fields
    if (columnLabels[columnId]) {
      // These columns get sort buttons: notes, industry, location, stage, status, tag
      const sortableColumns = ['notes', 'industry', 'location', 'stage', 'status', 'tag'];
      if (sortableColumns.includes(columnId)) {
        const w = columnWidths[columnId] || 150;
        return (
          <th
            key={columnId}
            className={`${columnId}-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
            style={{ width: w, minWidth: w, maxWidth: w, position: 'relative' }}
            data-column={columnId}
            draggable={!!onReorderColumns}
            onDragStart={(e) => handleDragStart(e, columnId)}
            onDragOver={(e) => handleDragOver(e, columnId)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, columnId)}
            onDragEnd={handleDragEnd}
          >
            <div className="header-content">
              <span className="column-label">{columnLabels[columnId]}</span>
              <button
                className="sort-button"
                onClick={() => onSort(columnId as SortField)}
                title={`Sort by ${columnLabels[columnId]}`}
              >
                {getSortIcon(columnId as SortField)}
              </button>
              {onToggleColumn && (
                <button
                  className="column-close-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleColumn(columnId);
                  }}
                  title={`Hide ${columnLabels[columnId]}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, columnId)} role="presentation" />
          </th>
        );
      }

      const w = columnWidths[columnId] || 150;
      return (
        <th
          key={columnId}
          className={`${columnId}-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
          style={{ width: w, minWidth: w, maxWidth: w, position: 'relative' }}
          data-column={columnId}
          draggable={!!onReorderColumns}
          onDragStart={(e) => handleDragStart(e, columnId)}
          onDragOver={(e) => handleDragOver(e, columnId)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, columnId)}
          onDragEnd={handleDragEnd}
        >
          <div className="header-content">
            <span className="column-label">{columnLabels[columnId]}</span>
            {onToggleColumn && (
              <button
                className="column-close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleColumn(columnId);
                }}
                title={`Hide ${columnLabels[columnId]}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, columnId)} role="presentation" />
        </th>
      );
    }

    // Check for custom fields LAST (after built-in columns)
    const field = getCustomFieldDefinition(columnId);
    if (field) {
      const w = columnWidths[columnId] || 150;
      return (
        <th
          key={columnId}
          className={`custom-field-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
          style={{ width: w, minWidth: w, maxWidth: w, position: 'relative' }}
          data-column={columnId}
          draggable={!!onReorderColumns}
          onDragStart={(e) => handleDragStart(e, columnId)}
          onDragOver={(e) => handleDragOver(e, columnId)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, columnId)}
          onDragEnd={handleDragEnd}
        >
          <div className="header-content">
            <span className="column-label">{field.name}</span>
            {onToggleColumn && (
              <button
                className="column-close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleColumn(columnId);
                }}
                title={`Hide ${field.name}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, columnId)} role="presentation" />
        </th>
      );
    }

    // Fallback: render header for any unrecognized column using columnId as label
    const w = columnWidths[columnId] || 150;
    const fallbackLabel = columnLabels[columnId] || columnId.charAt(0).toUpperCase() + columnId.slice(1).replace(/([A-Z])/g, ' $1').trim();
    return (
      <th
        key={columnId}
        className={`${columnId}-column ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
        style={{ width: w, minWidth: w, maxWidth: w, position: 'relative' }}
        data-column={columnId}
        draggable={!!onReorderColumns}
        onDragStart={(e) => handleDragStart(e, columnId)}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, columnId)}
        onDragEnd={handleDragEnd}
      >
        <div className="header-content">
          <span className="column-label">{fallbackLabel}</span>
          {onToggleColumn && (
            <button
              className="column-close-button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleColumn(columnId);
              }}
              title={`Hide ${fallbackLabel}`}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, columnId)} role="presentation" />
      </th>
    );
  };

  return (
    <thead>
      <tr className="table-header">
        <th className="checkbox-column" style={{ width: columnWidths.checkbox }}>
          {selectedIds.size > 0 ? (
            <div className="bulk-actions-header">
              <button
                className="bulk-delete-button-header"
                onClick={onBulkDelete}
                title={`Delete ${selectedIds.size} selected founder${selectedIds.size > 1 ? 's' : ''}`}
              >
                <Trash2 size={16} />
              </button>
              <button
                className="bulk-clear-button"
                onClick={() => onToggleSelectAll(false)}
                title="Clear selection"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <input
              type="checkbox"
              className="header-checkbox"
              onChange={(e) => onToggleSelectAll(e.target.checked)}
              title="Select all"
            />
          )}
          <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, 'checkbox')} />
        </th>
        {visibleColumns.includes('no') && (
          <th className="no-column" style={{ width: columnWidths.no || 60 }}>
            <div className="header-content">
              <span>NO</span>
              {onToggleColumn && (
                <button
                  className="column-close-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleColumn('no');
                  }}
                  title="Hide No"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, 'no')} />
          </th>
        )}
        {visibleColumns.includes('name') && renderColumnHeader('name')}
        {visibleColumns.includes('email') && renderColumnHeader('email')}
        {visibleColumns
          .filter(col => col !== 'no' && col !== 'name' && col !== 'email' && col !== 'update')
          .map(renderColumnHeader)
          .filter(header => header !== null)}
        {visibleColumns.includes('update') && (
          <th 
            className="update-column"
            style={{ width: columnWidths.update }}
            data-column="update"
          >
            <div className="header-content">
              <span>Update</span>
              <div className="job-queue-status-inline">
                <Circle 
                  size={6} 
                  fill={jobQueueStatus && (jobQueueStatus.running > 0) ? '#EF4444' : '#10B981'} 
                  color={jobQueueStatus && (jobQueueStatus.running > 0) ? '#EF4444' : '#10B981'}
                />
                <span>{getJobStatusText()} (Refresh to see updates)</span>
              </div>
              <button 
                className="refresh-button"
                onClick={handleRefreshAllClick}
                disabled={refreshingAll}
                title={refreshingAll ? "Refreshing all profiles..." : "Refresh all AI summaries"}
              >
                <RefreshCw size={14} className={refreshingAll ? "spinning" : ""} />
              </button>
              {onToggleColumn && (
                <button
                  className="column-close-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleColumn('update');
                  }}
                  title="Hide Update"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="resize-handle" onMouseDown={(e) => onMouseDown(e, 'update')} />
          </th>
        )}
      </tr>
      
      {/* Refresh All Confirmation Modal - Using Portal to render outside table */}
      {showRefreshAllModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Refresh All LinkedIn Profiles</h3>
            </div>
            <div className="modal-body">
              <p>You will refresh all profiles LinkedIn updates</p>
              <p>This will queue refresh jobs for all founders and process them one at a time in the background.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button modal-button-cancel" 
                onClick={handleCancelRefreshAll}
              >
                Cancel
              </button>
              <button 
                className="modal-button modal-button-confirm" 
                onClick={handleConfirmRefreshAll}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </thead>
  );
};

export default TableHeader;
